import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import { PropertyIdFilterOption } from "../components/PropertyIdFilter";
import { DEFAULT_PROPERTY_ANALYTICS_PERIOD } from "../constants/dashboardPeriods";
import { fetchPropertyAnalytics } from "../services/ownerPatrimonyService";
import {
  DashboardPeriodPreset,
  IOwnerKenloEndpoints,
  IPropertyAnalyticsData,
  OwnerPatrimonyRequestStatus,
} from "../types/ownerDashboard.types";
import { PropertyAnalyticsQuery } from "../utils/buildPropertyAnalyticsParams";
import { buildPropertyAnalyticsFilterOptions } from "../utils/buildPropertyAnalyticsFilterOptions";

interface UsePropertyAnalyticsOptions {
  accessToken: string | null;
  enabled: boolean;
  endpoints?: IOwnerKenloEndpoints;
}

interface UsePropertyAnalyticsResult {
  data: IPropertyAnalyticsData | null;
  status: OwnerPatrimonyRequestStatus;
  isConfigured: boolean | null;
  periodPreset: DashboardPeriodPreset;
  propertyId: number | null;
  propertyOptions: PropertyIdFilterOption[];
  setPeriodPreset: (period: DashboardPeriodPreset) => void;
  setPropertyId: (propertyId: number | null) => void;
  retry: () => Promise<void>;
}

export function usePropertyAnalytics({
  accessToken,
  enabled,
  endpoints,
}: UsePropertyAnalyticsOptions): UsePropertyAnalyticsResult {
  const [data, setData] = useState<IPropertyAnalyticsData | null>(null);
  const [status, setStatus] = useState<OwnerPatrimonyRequestStatus>("idle");
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [periodPreset, setPeriodPresetState] = useState(DEFAULT_PROPERTY_ANALYTICS_PERIOD);
  const [propertyId, setPropertyIdState] = useState<number | null>(null);
  const [propertyOptions, setPropertyOptions] = useState<PropertyIdFilterOption[]>([]);
  const latestRequestIdRef = useRef(0);
  const queryRef = useRef<PropertyAnalyticsQuery>({
    periodPreset: DEFAULT_PROPERTY_ANALYTICS_PERIOD,
    propertyId: null,
  });

  useEffect(() => {
    queryRef.current = {
      periodPreset,
      propertyId,
    };
  }, [periodPreset, propertyId]);

  const loadPropertyAnalytics = useCallback(async () => {
    if (!enabled || !accessToken) {
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setStatus("loading");

    try {
      api.defaults.headers.authorization = `Bearer ${accessToken}`;
      const response = await fetchPropertyAnalytics(queryRef.current, endpoints);

      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      setData(response);
      setIsConfigured(response.configured);
      setStatus("success");

      if (queryRef.current.propertyId == null && response.items.length > 0) {
        setPropertyOptions(buildPropertyAnalyticsFilterOptions(response.items));
      }
    } catch {
      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      setStatus("error");
    }
  }, [accessToken, enabled, endpoints]);

  const setPeriodPreset = useCallback((nextPeriod: DashboardPeriodPreset) => {
    setPeriodPresetState(nextPeriod);
  }, []);

  const setPropertyId = useCallback((nextPropertyId: number | null) => {
    setPropertyIdState(nextPropertyId);
  }, []);

  useEffect(() => {
    if (!enabled || !accessToken) {
      setData(null);
      setStatus("idle");
      setIsConfigured(null);
      setPropertyOptions([]);
      return;
    }

    void loadPropertyAnalytics();
  }, [accessToken, enabled, periodPreset, propertyId, loadPropertyAnalytics]);

  return {
    data,
    status,
    isConfigured,
    periodPreset,
    propertyId,
    propertyOptions,
    setPeriodPreset,
    setPropertyId,
    retry: loadPropertyAnalytics,
  };
}
